import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ExpandLess, ExpandMore, PictureAsPdf } from '@mui/icons-material';
import { Box, Button, Collapse, IconButton, styled } from '@mui/material';
import html2pdf from 'html2pdf.js';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Page } from 'src/components/Page';
import { IMenu, useStores } from 'src/models';
import {
  DEFAULT_MENU_SID,
  DEFAULT_PARENT_MENU_SID,
  getMenuType,
  GUIDE_CONTENT,
} from 'src/static/constants/guide/guideContent';

const Guide = () => {
  const { authStore, loadingStore } = useStores();
  const previewRef = useRef<HTMLDivElement>(null);

  // 로컬스토리지에 저장된 메뉴 가져오기
  const savedMenuSid = localStorage.getItem('selectedMenuSid');
  const savedParentId = localStorage.getItem('selectedParentId');
  // 초기값 설정 (저장된 값이 있으면 사용, 없으면 기본값 DEFAULT_PARENT_MENU_SID(notice) 사용)
  const initialParentId = savedParentId || String(DEFAULT_PARENT_MENU_SID);
  const initialMenuSid = savedMenuSid ? Number(savedMenuSid) : DEFAULT_MENU_SID;

  // 열려있을 부모 nav
  const [expandedSections, setExpandedSections] = useState<string[]>([String(initialParentId)]);
  // 선택한 nav
  const [selectedNav, setSelectedNav] = useState<number>(initialMenuSid);
  const [markdownContent, setMarkdownContent] = useState('');

  // 네비게이션 메뉴 가져오기, nav에 카테고리 추가하기
  const navConfig = useMemo(() => {
    const menuList = [...toJS(authStore.user?.menuList ?? [])] as unknown as IMenu[];

    // 카테고리 메뉴 추가
    const categoryMenu: IMenu = {
      menuNm: 'Category',
      menuPsid: 14,
      menuSid: 21,
      menuTypeCd: {
        code: 10401,
        pcode: 104,
        value: '권한메뉴',
      },
      menuUri: '/management/category',
      ordr: 4,
    } as unknown as IMenu;
    // 가이드 메뉴 추가
    const guideParentMenu: IMenu = {
      menuNm: 'Guide',
      menuPsid: null,
      menuSid: 22,
      menuTypeCd: {
        code: 10401,
        pcode: 104,
        value: '권한메뉴',
      },
      menuUri: '/management/guide',
      ordr: 4,
    } as unknown as IMenu;
    const guideMenu: IMenu = {
      menuNm: 'Guide',
      menuPsid: 22,
      menuSid: 23,
      menuTypeCd: {
        code: 10401,
        pcode: 104,
        value: '권한메뉴',
      },
      menuUri: '/management/guide',
      ordr: 4,
    } as unknown as IMenu;

    return [...menuList, categoryMenu, guideParentMenu, guideMenu];
  }, [authStore.user?.menuList]);

  // 초기 콘텐츠 설정
  useEffect(() => {
    const menuType = getMenuType(initialMenuSid);
    if (menuType) {
      // 컨텐츠
      setMarkdownContent(GUIDE_CONTENT[menuType].content);
      // 열려있을 부모 nav
      setExpandedSections([String(initialParentId)]);
    }
  }, [initialMenuSid, initialParentId]);

  const handleToggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((item) => item !== section) : [...prev, section],
    );
  };

  const handleSelectItem = (menuSid: number) => {
    // 선택한 menuSid 저장
    setSelectedNav(menuSid); // 1. state 저장
    localStorage.setItem('selectedMenuSid', String(menuSid)); // 2. 로컬스토리지 저장

    const parentId = navConfig.find((item) => item.menuSid === menuSid)?.menuPsid;
    if (parentId) {
      setExpandedSections((prev) => [...prev, String(menuSid)]);
      localStorage.setItem('selectedParentId', String(parentId));
    }

    // 선택한 menuSid에 해당하는 컨텐츠 가져오기
    const menuType = getMenuType(menuSid);
    const content = menuType ? GUIDE_CONTENT[menuType].content : '준비중입니다.';
    setMarkdownContent(content);

    // 스크롤을 최상단으로 이동
    if (previewRef.current) {
      previewRef.current.scrollTop = 0;
    }
  };
  // PDF 다운로드
  const handlePdfDownload = async () => {
    if (!previewRef.current) return;

    const options = {
      margin: 10,
      filename: 'homepage-guide.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 1.2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: false },
      // 페이지 나눔 개선
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }, // 가능한 요소 나누지 않음 / CSS 페이지 나눔 규칙 / 기존 페이지 나눔 로직 사용
    };

    html2pdf()
      .from(previewRef.current)
      .set(options)
      .save()
      .then(() => {
        if (previewRef.current) {
          previewRef.current.style.transform = '';
        }
      });
  };

  return (
    <Page>
      <StyledContainer>
        <Box sx={{ position: 'relative' }}>
          {/* 좌측 Nav 목록 */}
          <StyledNavSection>
            <StyledNavList>
              {navConfig
                .filter((item) => item.menuPsid === null)
                .map((section) => {
                  const childMenus = toJS(navConfig).filter(
                    (item) => item.menuPsid === section.menuSid,
                  );
                  const sectionId = section.menuSid?.toString() ?? '';
                  return (
                    <Box key={section.menuSid}>
                      <StyledNavHeader
                        onClick={() => handleToggleSection(section.menuSid?.toString() ?? '9')}
                      >
                        <IconButton size="small">
                          {expandedSections.includes(sectionId) ? (
                            <ExpandLess style={{ fontSize: '16px' }} />
                          ) : (
                            <ExpandMore style={{ fontSize: '16px' }} />
                          )}
                        </IconButton>
                        {section.menuNm}
                      </StyledNavHeader>

                      <Collapse in={expandedSections.includes(sectionId)}>
                        {childMenus.map((child) => {
                          return (
                            <StyledNavItem
                              key={child.menuSid}
                              onClick={() => handleSelectItem(child.menuSid ?? 0)}
                              selected={selectedNav === child.menuSid}
                            >
                              {child.menuNm}
                            </StyledNavItem>
                          );
                        })}
                      </Collapse>
                    </Box>
                  );
                })}
            </StyledNavList>
          </StyledNavSection>
          {/* PDF 다운로드 버튼 */}
          <StyledPdfButton>
            <Button
              fullWidth
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={() => handlePdfDownload()}
            >
              PDF 다운로드
            </Button>
          </StyledPdfButton>
        </Box>
        {/* view */}
        <StyledPreviewSection ref={previewRef} id="preview-section">
          <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
            {markdownContent}
          </ReactMarkdown>
        </StyledPreviewSection>
      </StyledContainer>
    </Page>
  );
};

export default observer(Guide);

const StyledContainer = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '200px 1fr',
  gap: '20px',
  height: '100%',
});

// PDF 버튼 스타일
const StyledPdfButton = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 24,
  right: 24,
});

const StyledNavSection = styled(Box)({
  position: 'relative',
  borderRight: '1px solid #e0e0e0',
  padding: '24px',
  maxHeight: '430px',
  overflow: 'auto',
  boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
});

const StyledNavList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginBottom: '60px', // PDF 버튼 공간 확보
});

const StyledNavHeader = styled(Box)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: '4px 0',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600',
}));

const StyledNavItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  padding: '5px 16px',
  cursor: 'pointer',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: '400',
  ...(selected && {
    backgroundColor: theme.palette.primary.lighter,
    color: theme.palette.common.white,
    fontWeight: '600',
  }),
}));

const StyledPreviewSection = styled(Box)({
  padding: '16px',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  overflow: 'auto',
  '& .guide-section': {
    pageBreakBefore: 'always',
    marginBottom: '40px',
  },
  '& .page-title': {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginTop: '12px',
    marginBottom: '24px',
  },
  '& ul, & ol': {
    paddingLeft: '20px',
  },
  '& ul ul, & ol ol, & ul ol, & ol ul': {
    paddingLeft: '20px',
  },
  '& li': {
    marginBottom: '6px',
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    marginBottom: '12px',
    fontSize: '0.9em',
  },
  '& th, & td': {
    border: '1px solid #ddd',
    padding: '8px',
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: '#f5f5f5',
  },
  '& img': {
    maxWidth: '800px',
    width: '100%',
    height: 'auto',
  },
  '& h1': {
    fontSize: '24px',
    marginTop: '32px',
    marginBottom: '16px',
  },
  '& h2': {
    fontSize: '20px',
    marginTop: '24px',
    marginBottom: '12px',
  },
  '& h3': {
    fontSize: '16px',
    marginTop: '20px',
    marginBottom: '10px',
  },
  '& p': {
    marginBottom: '8px',
    lineHeight: '1.5',
  },
});
